import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const QUICK_CAPTURE_STORAGE_KEY = 'eis_quick_message_capture';
const authHeaders = (extra: Record<string, string> = {}) => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'X-CSRF-Token': getCsrfToken(),
  ...extra,
});
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import {
  MessageSquare,
  Search,
  Send,
  Plus,
  Paperclip,
  Camera,
  Download,
  X,
  RefreshCw,
  Sparkles,
  Wand2,
  RotateCcw,
  Trash2,
  Inbox,
  AlertTriangle,
  Pencil,
  Mic,
} from 'lucide-react';


interface MessageAttachment {
  storedName?: string;
  fileName: string;
  originalName?: string;
  mimeType: string;
  size: number;
  url: string;
  uploadedAt?: string;
  isScreenshot?: boolean;
}

interface MessageMetadata {
  attachments?: MessageAttachment[];
  [key: string]: any;
}

interface ScreenshotHighlight {
  x: number; // normalized 0..1
  y: number; // normalized 0..1
  width: number; // normalized 0..1
  height: number; // normalized 0..1
}

interface Message {
  id: number;
  type: 'request' | 'feedback' | 'notification' | 'reminder';
  title: string;
  message: string;
  from: {
    id?: number;
    name: string;
    role: string;
  };
  to?: {
    id?: number;
    name: string;
    role: string;
  };
  isRead: boolean;
  readByRecipient: boolean; // true when the recipient has read it (for sent tab ticks)
  isSent: boolean;          // true when current user is the sender
  createdAt: string;
  related?: { type: string; name: string };
  priority?: 'low' | 'medium' | 'high';
  parentMessageId?: number | null;
  replyCount?: number;
  metadata?: MessageMetadata;
}

interface MessageReaction {
  emoji: string;
  count: number;
  user_ids: number[];
  reacted_by_me: boolean;
}

interface ThreadMessage {
  id: number;
  senderId: number;
  recipientId: number;
  subject: string;
  content: string;
  messageType: string;
  priority: string;
  isRead: boolean;
  readAt: string | null;
  parentMessageId: number | null;
  createdAt: string;
  metadata?: MessageMetadata;
  reactions?: MessageReaction[];
  sender: { id: number; firstName: string; lastName: string; email: string };
  recipient: { id: number; firstName: string; lastName: string; email: string };
}

const ReadReceipt: React.FC<{ isRead: boolean; readAt: string | null; isMine: boolean }> = ({ isRead, readAt, isMine }) => {
  if (!isMine) return null;
  if (isRead && readAt) {
    const readTime = new Date(readAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    return (
      <div className="flex items-center gap-1 mt-1" title={`Read ${readTime}`}>
        <svg className="w-3.5 h-3.5 text-blue-500" viewBox="0 0 16 16" fill="none">
          <path d="M1.5 8.5l3 3L9 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5.5 8.5l3 3L13 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-[10px] text-blue-500 font-medium">Read {readTime}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 mt-1" title="Delivered — not yet read">
      <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 16 16" fill="none">
        <path d="M2 8.5l4 4L14 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span className="text-[10px] text-slate-400">Delivered</span>
    </div>
  );
};

// Reaction emoji set — keep tight so the picker is 1-click scannable.
// Matches the Slack/Linear default reaction row.
const REACTION_EMOJIS = ['👍', '❤️', '🎉', '😄', '😮', '🙏'] as const;

const Messages: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'unread' | 'read'>('all');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [activeTab, setActiveTab] = useState<'inbox' | 'sent' | 'feedback'>(() => {
    // Respect ?tab=sent|feedback from the URL on first mount so a reload
    // with ?id=<n>&tab=sent restores the correct list view (otherwise we'd
    // open the inbox tab and fail to find a sent-only message).
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'sent' || t === 'feedback' ? t : 'inbox';
  });
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [resolutionText, setResolutionText] = useState('');

  // Compose message state
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingQuickReplyAttachment, setPendingQuickReplyAttachment] = useState<MessageAttachment | null>(null);
  const [messagingPermissions, setMessagingPermissions] = useState<any>(null);
  const [availableRecipients, setAvailableRecipients] = useState<any[]>([]);
  const [composeForm, setComposeForm] = useState({
    recipientId: '',
    subject: '',
    content: '',
    messageType: 'direct_message',
    priority: 'normal',
  });
  const [feedbackForm, setFeedbackForm] = useState({
    subject: '',
    content: '',
    priority: 'normal',
  });
  const [composeAttachments, setComposeAttachments] = useState<MessageAttachment[]>([]);
  const [feedbackAttachments, setFeedbackAttachments] = useState<MessageAttachment[]>([]);
  const [replyAttachments, setReplyAttachments] = useState<MessageAttachment[]>([]);
  const [uploadingComposeAttachment, setUploadingComposeAttachment] = useState(false);
  const [uploadingFeedbackAttachment, setUploadingFeedbackAttachment] = useState(false);
  const [uploadingReplyAttachment, setUploadingReplyAttachment] = useState(false);
  const [screenshotEditorTarget, setScreenshotEditorTarget] = useState<'compose' | 'feedback' | 'reply' | null>(null);
  const [screenshotEditorImage, setScreenshotEditorImage] = useState<string | null>(null);
  const [screenshotHighlights, setScreenshotHighlights] = useState<ScreenshotHighlight[]>([]);
  const [draftScreenshotHighlight, setDraftScreenshotHighlight] = useState<ScreenshotHighlight | null>(null);
  const [screenshotHighlightStart, setScreenshotHighlightStart] = useState<{ x: number; y: number } | null>(null);
  const [savingMarkedScreenshot, setSavingMarkedScreenshot] = useState(false);
  const composeFileInputRef = useRef<HTMLInputElement>(null);
  const feedbackFileInputRef = useRef<HTMLInputElement>(null);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const screenshotOverlayRef = useRef<HTMLDivElement>(null);

  // AI assist state
  const [isAILoading, setIsAILoading] = useState(false);

  const [isSending, setIsSending] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ subject: '', content: '' });

  // Thread / conversation state
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  // ── Voice dictation (Web Speech API, live transcription) ──────────────────
  // Browsers that expose SpeechRecognition / webkitSpeechRecognition (Chrome,
  // Edge, and Safari on recent macOS/iOS) stream interim + final transcripts
  // as the user speaks. We append final segments to whatever text is already
  // in the target field so existing drafts aren't lost, and render interim
  // results live so the user sees their words appear as they talk.
  type DictationTarget = 'reply' | 'compose' | 'feedback';
  const [recordingTarget, setRecordingTarget] = useState<DictationTarget | null>(null);
  const recognitionRef = useRef<any>(null);
  const recordingTargetRef = useRef<DictationTarget | null>(null);
  const dictationBaselineRef = useRef<string>('');
  const dictationSupported = typeof window !== 'undefined' &&
    ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  const applyDictationText = useCallback((target: DictationTarget, value: string) => {
    if (target === 'reply') {
      setReplyText(value);
    } else if (target === 'compose') {
      setComposeForm((p) => ({ ...p, content: value }));
    } else {
      setFeedbackForm((p) => ({ ...p, content: value }));
    }
  }, []);

  const stopDictation = useCallback(() => {
    const target = recordingTargetRef.current;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
      recognitionRef.current = null;
    }
    if (target) {
      // Commit the finalized baseline (drops any hanging interim text).
      applyDictationText(target, dictationBaselineRef.current);
    }
    recordingTargetRef.current = null;
    setRecordingTarget(null);
  }, [applyDictationText]);

  const startDictation = useCallback((target: DictationTarget, currentValue: string) => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice recording is not supported in this browser. Try Chrome, Edge or Safari.');
      return;
    }
    // If another field is already recording, stop it first so we don't leak
    // the previous recognizer.
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
      recognitionRef.current = null;
    }

    dictationBaselineRef.current = currentValue || '';
    recordingTargetRef.current = target;

    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';

    recognition.onresult = (event: any) => {
      let finalChunk = '';
      let interimChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalChunk += transcript;
        else interimChunk += transcript;
      }
      if (finalChunk) {
        const needsSpace = dictationBaselineRef.current && !/\s$/.test(dictationBaselineRef.current);
        dictationBaselineRef.current = dictationBaselineRef.current + (needsSpace ? ' ' : '') + finalChunk.trim();
      }
      const needsInterimSpace = dictationBaselineRef.current && interimChunk && !/\s$/.test(dictationBaselineRef.current);
      const combined = dictationBaselineRef.current + (interimChunk ? (needsInterimSpace ? ' ' : '') + interimChunk : '');
      applyDictationText(target, combined);
    };

    recognition.onerror = (event: any) => {
      const err = event?.error;
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        toast.error('Microphone access denied. Allow mic access in your browser to use voice dictation.');
      } else if (err && err !== 'no-speech' && err !== 'aborted') {
        toast.error(`Voice recording error: ${err}`);
      }
    };

    recognition.onend = () => {
      // Fires on manual stop, error, or silence timeout. Commit final text.
      const t = recordingTargetRef.current;
      if (t) {
        applyDictationText(t, dictationBaselineRef.current);
      }
      recognitionRef.current = null;
      recordingTargetRef.current = null;
      setRecordingTarget(null);
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setRecordingTarget(target);
    } catch {
      toast.error('Could not start voice recording. Try again.');
      recordingTargetRef.current = null;
    }
  }, [applyDictationText]);

  const toggleDictation = useCallback((target: DictationTarget, currentValue: string) => {
    if (recordingTargetRef.current === target) {
      stopDictation();
    } else {
      startDictation(target, currentValue);
    }
  }, [startDictation, stopDictation]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* noop */ }
        recognitionRef.current = null;
      }
    };
  }, []);

  // Ref so polling closure always sees the current tab without stale capture
  const activeTabRef = useRef(activeTab);

  const isSystemAdmin = hasPermission('system.config');
  const isCompanyAdmin =
    (hasPermission('companies.update') || hasPermission('companies.create') || hasPermission('users.create')) &&
    !isSystemAdmin;

  // Banner shown when an email link (?id=N) arrives but the message isn't
  // in the current user's inbox — prevents a silent empty view / apparent 404.
  const [deepLinkMissing, setDeepLinkMissing] = useState<string | null>(null);

  // ─── Deep-link: auto-select message from URL ?id=xxx ───────────────────────
  // Searches the current tab's messages first. If not found, checks via API
  // whether the message belongs to this user on another tab (e.g. Sent) and
  // switches automatically. Only shows the "not in your inbox" banner if the
  // message truly isn't accessible.
  const deepLinkCheckedRef = useRef<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    if (!id) {
      setDeepLinkMissing(null);
      deepLinkCheckedRef.current = null;
      return;
    }
    if (messages.length === 0) return; // still loading — wait
    const msg = messages.find((m) => String(m.id) === id);
    if (msg) {
      setSelectedMessage(msg);
      if (!msg.isRead) markAsRead(msg.id);
      setDeepLinkMissing(null);
      deepLinkCheckedRef.current = null;
    } else if (deepLinkCheckedRef.current !== id) {
      // Not in current tab — try switching tab before showing banner
      deepLinkCheckedRef.current = id;
      if (activeTab === 'inbox') {
        setActiveTab('sent');
      } else if (activeTab === 'sent') {
        setActiveTab('inbox');
      } else {
        setDeepLinkMissing(id);
      }
    } else {
      // Already tried switching tabs — message truly not found
      setDeepLinkMissing(id);
    }
  }, [location.search, messages, activeTab]);

  // Keep the URL in sync with the open message so a reload restores it.
  // We only *write* to the URL when a message is actually open — we never
  // proactively strip ?id=<n> just because selectedMessage is null. That
  // avoids a race on hard-reload where the URL-sync effect would wipe the
  // deep-link id before the deep-link effect had the inbox list to match
  // against. When the user explicitly closes a message, the close handler
  // below clears the param.
  useEffect(() => {
    if (!selectedMessage) return;
    const params = new URLSearchParams(location.search);
    const nextId = String(selectedMessage.id);
    const currentId = params.get('id');
    const currentTab = params.get('tab');
    const nextTab = activeTab === 'inbox' ? null : activeTab;
    if (currentId === nextId && currentTab === nextTab) return;
    params.set('id', nextId);
    if (nextTab) params.set('tab', nextTab); else params.delete('tab');
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [selectedMessage?.id, activeTab]);

  const clearIdFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('id') && !params.has('tab')) return;
    params.delete('id');
    params.delete('tab');
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Auto-resize reply textarea when content changes (e.g. from AI)
  useEffect(() => {
    const el = replyTextareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 256) + 'px';
    }
  }, [replyText]);

  // Keep the tab ref current so polling closure never reads a stale value
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  // ─── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadMessages();
    loadMessagingPermissions();
  }, [activeTab]);

  useEffect(() => {
    checkForNewMessages();
    const poll = setInterval(checkForNewMessages, 30000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (messagingPermissions) loadAvailableRecipients();
  }, [messagingPermissions]);

  useEffect(() => {
    filterMessages();
  }, [messages, searchTerm, filterType]);

  useEffect(() => {
    if (!isSystemAdmin && !isCompanyAdmin) {
      const autoRecipient = getAutoRecipient(composeForm.messageType);
      if (autoRecipient) setComposeForm((prev) => ({ ...prev, recipientId: autoRecipient }));
    }
  }, [composeForm.messageType, availableRecipients, isSystemAdmin, isCompanyAdmin]);

  function addAttachmentOnce(attachments: MessageAttachment[], candidate: MessageAttachment) {
    const exists = attachments.some((attachment) => {
      if (attachment.storedName && candidate.storedName) return attachment.storedName === candidate.storedName;
      return attachment.url === candidate.url;
    });
    return exists ? attachments : [...attachments, candidate];
  }

  const clearQuickCaptureQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('quickCapture') && !params.has('ts')) return;
    params.delete('quickCapture');
    params.delete('ts');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const quickCaptureMode = params.get('quickCapture');
    if (quickCaptureMode !== 'new' && quickCaptureMode !== 'existing') return;

    const raw = sessionStorage.getItem(QUICK_CAPTURE_STORAGE_KEY);
    if (!raw) {
      toast.error('Captured screenshot was not found. Please capture it again.');
      clearQuickCaptureQuery();
      return;
    }

    try {
      const parsed = JSON.parse(raw) as { attachment?: MessageAttachment };
      const attachment = parsed?.attachment;
      if (!attachment?.url) throw new Error('Invalid screenshot payload');

      if (quickCaptureMode === 'new') {
        setComposeAttachments((prev) => addAttachmentOnce(prev, attachment));
        setShowComposeModal(true);
        toast.success('Screenshot attached to new message');
      } else {
        setPendingQuickReplyAttachment(attachment);
        setSelectedMessage(null);
        setReplyAttachments([]);
        setActiveTab('inbox');
        toast.info('Select a conversation to attach this screenshot and reply.');
      }
    } catch {
      toast.error('Could not prepare the captured screenshot');
    } finally {
      sessionStorage.removeItem(QUICK_CAPTURE_STORAGE_KEY);
      clearQuickCaptureQuery();
    }
  }, [clearQuickCaptureQuery, location.search]);

  const formatTimestamp = (ts: any) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '';
      const now = new Date();
      const isToday =
        d.getDate() === now.getDate() &&
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear();
      return isToday
        ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
        : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    } catch {
      return '';
    }
  };

  const parseMetadata = (raw: any): MessageMetadata => {
    if (!raw) return {};
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch {
        return {};
      }
    }
    return typeof raw === 'object' ? raw : {};
  };

  const extractAttachments = (raw: any): MessageAttachment[] => {
    const metadata = parseMetadata(raw);
    return Array.isArray(metadata.attachments)
      ? metadata.attachments.filter((a: any) => a && typeof a === 'object' && typeof a.url === 'string')
      : [];
  };

  const isMessageEdited = (rawMetadata: any): boolean => {
    const metadata = parseMetadata(rawMetadata);
    const editInfo = metadata.edit_info;
    return metadata.isEdited === true || (editInfo && typeof editInfo === 'object' && editInfo.edited === true);
  };

  const getMessageEditedAt = (rawMetadata: any): string | null => {
    const metadata = parseMetadata(rawMetadata);
    const editInfo = metadata.edit_info;
    const candidate =
      (editInfo && typeof editInfo === 'object' ? editInfo.editedAt : undefined) ||
      metadata.editedAt ||
      metadata.lastEditedAt;
    return typeof candidate === 'string' && candidate.trim() ? candidate : null;
  };

  const canEditSentMessage = (msg: Message): boolean => {
    if (!msg?.isSent) return false;
    if (msg.type === 'feedback') return false;
    return (msg.replyCount || 0) === 0;
  };

  const formatFileSize = (size: number) => {
    if (!Number.isFinite(size) || size <= 0) return '0 B';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const clampUnit = (value: number) => Math.max(0, Math.min(1, value));

  const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Could not load screenshot'));
      reader.readAsDataURL(file);
    });

  const closeScreenshotEditor = () => {
    setScreenshotEditorTarget(null);
    setScreenshotEditorImage(null);
    setScreenshotHighlights([]);
    setDraftScreenshotHighlight(null);
    setScreenshotHighlightStart(null);
    setSavingMarkedScreenshot(false);
  };

  const getOverlayPoint = (event: React.MouseEvent<HTMLDivElement>) => {
    const overlay = screenshotOverlayRef.current;
    if (!overlay) return null;
    const rect = overlay.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: clampUnit((event.clientX - rect.left) / rect.width),
      y: clampUnit((event.clientY - rect.top) / rect.height),
    };
  };

  const beginScreenshotHighlight = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const point = getOverlayPoint(event);
    if (!point) return;
    setScreenshotHighlightStart(point);
    setDraftScreenshotHighlight({ x: point.x, y: point.y, width: 0, height: 0 });
  };

  const moveScreenshotHighlight = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!screenshotHighlightStart) return;
    const point = getOverlayPoint(event);
    if (!point) return;
    const x = Math.min(screenshotHighlightStart.x, point.x);
    const y = Math.min(screenshotHighlightStart.y, point.y);
    const width = Math.abs(point.x - screenshotHighlightStart.x);
    const height = Math.abs(point.y - screenshotHighlightStart.y);
    setDraftScreenshotHighlight({ x, y, width, height });
  };

  const endScreenshotHighlight = () => {
    if (!draftScreenshotHighlight) {
      setScreenshotHighlightStart(null);
      return;
    }
    if (draftScreenshotHighlight.width >= 0.01 && draftScreenshotHighlight.height >= 0.01) {
      setScreenshotHighlights((prev) => [...prev, draftScreenshotHighlight]);
    }
    setDraftScreenshotHighlight(null);
    setScreenshotHighlightStart(null);
  };

  const highlightToStyle = (highlight: ScreenshotHighlight): React.CSSProperties => ({
    left: `${highlight.x * 100}%`,
    top: `${highlight.y * 100}%`,
    width: `${highlight.width * 100}%`,
    height: `${highlight.height * 100}%`,
  });

  const createMarkedScreenshotFile = async (imageDataUrl: string, highlights: ScreenshotHighlight[]) => {
    const image = new Image();
    image.src = imageDataUrl;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Could not process screenshot'));
    });

    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not process screenshot');

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const lineWidth = Math.max(3, Math.round(Math.min(canvas.width, canvas.height) * 0.003));
    const labelSize = Math.max(14, lineWidth * 4);

    highlights.forEach((h, idx) => {
      const x = h.x * canvas.width;
      const y = h.y * canvas.height;
      const w = h.width * canvas.width;
      const hPx = h.height * canvas.height;

      ctx.fillStyle = 'rgba(251, 191, 36, 0.25)'; // amber overlay
      ctx.fillRect(x, y, w, hPx);

      ctx.strokeStyle = 'rgba(245, 158, 11, 0.95)'; // amber border
      ctx.lineWidth = lineWidth;
      ctx.strokeRect(x, y, w, hPx);

      const badgeText = String(idx + 1);
      ctx.font = `700 ${labelSize}px sans-serif`;
      const badgeWidth = ctx.measureText(badgeText).width + 10;
      const badgeHeight = labelSize + 6;
      const badgeX = Math.max(0, x);
      const badgeY = Math.max(0, y - badgeHeight);

      ctx.fillStyle = 'rgba(245, 158, 11, 0.95)';
      ctx.fillRect(badgeX, badgeY, badgeWidth, badgeHeight);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(badgeText, badgeX + 5, badgeY + badgeHeight - 5);
    });

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Could not generate screenshot');
    return new File([blob], `screenshot-marked-${Date.now()}.png`, { type: 'image/png' });
  };

  const saveMarkedScreenshotAndAttach = async () => {
    if (!screenshotEditorTarget || !screenshotEditorImage) return;
    setSavingMarkedScreenshot(true);
    try {
      const markedFile = await createMarkedScreenshotFile(screenshotEditorImage, screenshotHighlights);
      await handleAttachmentPick(screenshotEditorTarget, markedFile, { isScreenshot: true });
      closeScreenshotEditor();
    } catch (error: any) {
      toast.error(error?.message || 'Could not attach screenshot');
    } finally {
      setSavingMarkedScreenshot(false);
    }
  };

  const buildSafeUrl = (raw: string): string | null => {
    const candidate = raw.startsWith('www.') ? `https://${raw}` : raw;
    try {
      const parsed = new URL(candidate);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.toString();
      return null;
    } catch {
      return null;
    }
  };

  const renderMessageContentWithLinks = (text: string, linkClassName: string) => {
    const lines = text.split('\n');
    return lines.map((line, lineIndex) => (
      <React.Fragment key={`line-${lineIndex}`}>
        {line.split(/(https?:\/\/[^\s]+|www\.[^\s]+)/gi).map((chunk, chunkIndex) => {
          const isUrlCandidate = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/i.test(chunk);
          if (!isUrlCandidate) return <React.Fragment key={`chunk-${lineIndex}-${chunkIndex}`}>{chunk}</React.Fragment>;

          const core = chunk.replace(/[),.!?:;]+$/g, '');
          const trailing = chunk.slice(core.length);
          const safeUrl = buildSafeUrl(core);
          if (!safeUrl) return <React.Fragment key={`chunk-${lineIndex}-${chunkIndex}`}>{chunk}</React.Fragment>;

          return (
            <React.Fragment key={`chunk-${lineIndex}-${chunkIndex}`}>
              <a
                href={safeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClassName}
              >
                {core}
              </a>
              {trailing}
            </React.Fragment>
          );
        })}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const getAutoRecipient = (messageType: string): string | null => {
    if (isSystemAdmin || isCompanyAdmin) return null;
    if (messageType === 'feedback') {
      const sa = availableRecipients.find((u: any) => !u.company_id);
      return sa ? String(sa.id) : null;
    }
    return null;
  };

  const checkForNewMessages = async () => {
    try {
      // Must match the inbox query (exclude feedback) so the badge reflects
      // only what's visible in the Inbox tab
      const res = await fetch('/api/messaging?excludeType=feedback', { headers: authHeaders() });
      if (res.ok) {
        const result = await res.json();
        const msgs = result.data || [];
        const unread = msgs.filter((m: any) => !m.isRead).length;
        setInboxUnreadCount(unread);
        // Use ref so we never read a stale activeTab from the closure
        if (unread > 0 && activeTabRef.current !== 'inbox') {
          setHasNewMessages(true);
        } else if (unread === 0) {
          setHasNewMessages(false);
        }
      }
    } catch {}
  };

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      const apiUrl =
        activeTab === 'sent'
          ? '/api/messaging?sent=true&excludeType=feedback'
          : activeTab === 'feedback'
          ? '/api/messaging?messageType=feedback'
          : '/api/messaging?excludeType=feedback';

      const res = await fetch(apiUrl, { headers: authHeaders() });
      if (res.ok) {
        const result = await res.json();
        const formatted: Message[] = (result.data || []).map((msg: any) => {
          const metadata = parseMetadata(msg.metadata);
          return {
          id: msg.id,
          type:
            msg.messageType === 'compliance_request'
              ? 'request'
              : msg.messageType === 'feedback'
              ? 'feedback'
              : msg.messageType === 'reminder'
              ? 'reminder'
              : 'notification',
          title: msg.subject,
          message: msg.content,
          from: {
            id: msg.sender?.id,
            name: [msg.sender?.firstName, msg.sender?.lastName].filter(Boolean).join(' ') || msg.sender?.email || 'Unknown',
            role: String(msg.sender?.roles?.[0] || 'User'),
          },
          ...(activeTab === 'sent'
            ? {
                to: {
                  id: msg.recipient?.id,
                  name: [msg.recipient?.firstName, msg.recipient?.lastName].filter(Boolean).join(' ') || 'Unknown',
                  role: String(msg.recipient?.roles?.[0] || 'User'),
                },
              }
            : {}),
          // For sent messages, the current user is the sender — they never need to "mark as read"
          // isRead here means the *recipient* has read it (used for delivery ticks on sent tab)
          isSent: activeTab === 'sent',
          // A conversation is unread if the root message is unread OR there are unread replies
          isRead: activeTab === 'sent' ? true : (!!msg.isRead && !(msg.unreadReplyCount > 0)),
          readByRecipient: !!msg.isRead,
          createdAt: msg.createdAt,
          ...(msg.relatedEntityType ? { related: { type: String(msg.relatedEntityType), name: String(msg.relatedEntityId) } } : {}),
          priority: msg.priority === 'urgent' || msg.priority === 'high' ? 'high' : msg.priority === 'low' ? 'low' : 'medium',
          parentMessageId: msg.parentMessageId || null,
          replyCount: msg.replyCount || 0,
          metadata,
          };
        });

        if (activeTab === 'inbox') {
          const unread = formatted.filter((m) => !m.isRead).length;
          setInboxUnreadCount(unread);
          setHasNewMessages(false);
        }
        setMessages(formatted);
      } else {
        setMessages([]);
      }
    } catch {
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterMessages = () => {
    let f = messages;
    if (filterType === 'unread') f = f.filter((m) => !m.isRead);
    else if (filterType === 'read') f = f.filter((m) => m.isRead);
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      f = f.filter(
        (m) =>
          m.title.toLowerCase().includes(s) ||
          m.message.toLowerCase().includes(s) ||
          m.from.name.toLowerCase().includes(s)
      );
    }
    setFilteredMessages(f);
  };

  const markAsRead = async (id: number) => {
    // Find the message — skip if it's a sent message (sender doesn't mark-as-read; recipient does)
    const msg = messages.find((m) => m.id === id);
    if (!msg || msg.isSent) return;

    // Optimistic local update
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, isRead: true } : m)));
    setInboxUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      const res = await fetch(`/api/messaging/${id}/read`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!res.ok) {
        // Revert and reload authoritative state from server
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, isRead: false } : m)));
        setInboxUnreadCount((prev) => prev + 1);
        loadMessages();
      }
    } catch {
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, isRead: false } : m)));
      setInboxUnreadCount((prev) => prev + 1);
      loadMessages();
    }
  };

  const deleteMessage = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (deletingId) return;
    setDeletingId(id);
    setMessages((prev) => prev.filter((m) => m.id !== id));
    if (selectedMessage?.id === id) setSelectedMessage(null);
    try {
      const res = await fetch(`/api/messaging/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) { toast.error('Failed to delete'); loadMessages(); }
      else toast.success('Message deleted');
    } catch {
      toast.error('Failed to delete');
      loadMessages();
    } finally {
      setDeletingId(null);
    }
  };

  const loadMessagingPermissions = async () => {
    try {
      const res = await fetch('/api/messaging/permissions', { headers: authHeaders() });
      if (res.ok) { const r = await res.json(); setMessagingPermissions(r.data); }
    } catch {}
  };

  const loadAvailableRecipients = useCallback(async () => {
    if (!messagingPermissions) return;
    try {
      const res = await fetch('/api/messaging/recipients', { headers: authHeaders() });
      if (res.ok) { const r = await res.json(); setAvailableRecipients(r.data || []); }
    } catch {}
  }, [messagingPermissions]);

  const uploadAttachmentFile = async (file: File): Promise<MessageAttachment> => {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch('/api/messaging/attachments', {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(result.error || 'Attachment upload failed');
    return result.data as MessageAttachment;
  };

  const handleAttachmentPick = async (
    target: 'compose' | 'feedback' | 'reply',
    file?: File,
    options?: { isScreenshot?: boolean }
  ) => {
    if (!file) return;
    const setLoading =
      target === 'compose' ? setUploadingComposeAttachment :
      target === 'feedback' ? setUploadingFeedbackAttachment :
      setUploadingReplyAttachment;
    const setAttachments =
      target === 'compose' ? setComposeAttachments :
      target === 'feedback' ? setFeedbackAttachments :
      setReplyAttachments;

    setLoading(true);
    try {
      const uploaded = await uploadAttachmentFile(file);
      setAttachments((prev) => [...prev, { ...uploaded, isScreenshot: options?.isScreenshot || uploaded.isScreenshot }]);
      toast.success('Attachment added');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to upload attachment');
    } finally {
      setLoading(false);
    }
  };

  const removeAttachment = (target: 'compose' | 'feedback' | 'reply', index: number) => {
    const setAttachments =
      target === 'compose' ? setComposeAttachments :
      target === 'feedback' ? setFeedbackAttachments :
      setReplyAttachments;
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const captureScreenshotFile = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error('Screenshot capture is not supported in this browser');
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    try {
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => resolve();
      });
      await video.play();

      const width = video.videoWidth || window.innerWidth;
      const height = video.videoHeight || window.innerHeight;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not capture screen');
      ctx.drawImage(video, 0, 0, width, height);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Could not generate screenshot file');

      return new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
    } finally {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  const captureAndAttachScreenshot = async (target: 'compose' | 'feedback' | 'reply') => {
    try {
      const file = await captureScreenshotFile();
      const imageDataUrl = await fileToDataUrl(file);
      setScreenshotEditorTarget(target);
      setScreenshotEditorImage(imageDataUrl);
      setScreenshotHighlights([]);
      setDraftScreenshotHighlight(null);
      setScreenshotHighlightStart(null);
    } catch (error: any) {
      if (error?.name === 'NotAllowedError') {
        toast.info('Screenshot capture cancelled');
      } else {
        toast.error(error?.message || 'Could not capture screenshot');
      }
    }
  };

  const downloadAttachment = async (attachment: MessageAttachment) => {
    const storedName = attachment.storedName || attachment.fileName || attachment.url?.split('/').pop();
    if (!storedName) {
      toast.error('Attachment reference missing — cannot download');
      return;
    }

    try {
      const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
      const res = await fetch(`/api/messaging/attachments/${encodeURIComponent(storedName)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const mime = attachment.mimeType || blob.type || '';

      // Images and PDFs: open in a new browser tab for viewing
      // Everything else: download directly
      if (mime.startsWith('image/') || mime === 'application/pdf') {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      } else {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = attachment.originalName || attachment.fileName || storedName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not download attachment');
    }
  };

  // ─── Thread loading ─────────────────────────────────────────────────────────
  const loadThread = async (messageId: number) => {
    setThreadLoading(true);
    try {
      const res = await fetch(`/api/messaging/thread/${messageId}`, { headers: authHeaders() });
      if (res.ok) {
        const result = await res.json();
        const thread: ThreadMessage[] = (result.data || []).map((tm: any) => ({
          ...tm,
          metadata: parseMetadata(tm.metadata),
        }));
        setThreadMessages(thread);

        // Mark any unread messages in the thread as read (where I'm the recipient)
        const unreadInThread = thread.filter((tm) => !tm.isRead && tm.recipientId === user?.id);
        for (const tm of unreadInThread) {
          fetch(`/api/messaging/${tm.id}/read`, {
            method: 'PUT',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
          }).catch(() => {});
        }

        // Scroll to bottom after render
        setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      } else {
        setThreadMessages([]);
      }
    } catch {
      setThreadMessages([]);
    } finally {
      setThreadLoading(false);
    }
  };

  // ─── Reactions ─────────────────────────────────────────────────────────────
  /**
   * Toggle an emoji reaction on a thread message. POST hits the same
   * endpoint regardless of whether we're adding or removing — the
   * backend's toggle semantics mean we just send the emoji and it
   * figures out which operation to perform.
   *
   * The server returns the fresh aggregated reaction list for the
   * message so we replace it in local state in one place.
   */
  const toggleReaction = async (messageId: number, emoji: string) => {
    // Optimistic update — flip reacted_by_me + count before the network
    // call so the UI feels instant. If the request fails we roll back
    // from the server response anyway.
    setThreadMessages(prev => prev.map(m => {
      if (m.id !== messageId) return m;
      const existing = m.reactions ?? [];
      const hit = existing.find(r => r.emoji === emoji);
      let next: MessageReaction[];
      if (hit) {
        if (hit.reacted_by_me) {
          // I was reacting — removing myself
          const count = Math.max(0, hit.count - 1);
          next = count === 0
            ? existing.filter(r => r.emoji !== emoji)
            : existing.map(r => r.emoji === emoji ? { ...r, count, reacted_by_me: false, user_ids: r.user_ids.filter(u => u !== user?.id) } : r);
        } else {
          // Someone else had reacted — I'm adding myself
          next = existing.map(r => r.emoji === emoji
            ? { ...r, count: r.count + 1, reacted_by_me: true, user_ids: [...r.user_ids, user?.id ?? 0] }
            : r);
        }
      } else {
        // Brand-new reaction on this message
        next = [...existing, { emoji, count: 1, reacted_by_me: true, user_ids: [user?.id ?? 0] }];
      }
      return { ...m, reactions: next };
    }));

    try {
      const res = await fetch(`/api/messaging/${messageId}/reactions`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ emoji }),
      });
      if (!res.ok) {
        toast.error('Could not save reaction');
        // On failure, refresh from server to sync state.
        loadThread(messageId);
        return;
      }
      const data = await res.json();
      // Replace with authoritative server state.
      setThreadMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: data.reactions || [] } : m));
    } catch {
      toast.error('Could not save reaction');
      loadThread(messageId);
    }
  };

  const sendReply = async (parentMsg: Message) => {
    if (!replyText.trim() && replyAttachments.length === 0) return;
    setIsSendingReply(true);

    // Find the root message id and participants for this thread
    const rootThreadMessage = threadMessages[0];
    const rootId = rootThreadMessage?.id ?? parentMsg.id;

    // Determine recipient from thread participants.
    // If I'm the root sender, reply to the root recipient; otherwise reply to the root sender.
    const recipientId = rootThreadMessage
      ? (rootThreadMessage.senderId === user?.id ? rootThreadMessage.recipientId : rootThreadMessage.senderId)
      : (parentMsg.isSent ? parentMsg.to?.id : parentMsg.from.id);

    if (!recipientId) {
      toast.error('Could not determine recipient');
      setIsSendingReply(false);
      return;
    }

    try {
      const rootSubject = rootThreadMessage?.subject ?? parentMsg.title;
      const res = await fetch('/api/messaging', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recipientId,
          subject: rootSubject.startsWith('Re: ') ? rootSubject : `Re: ${rootSubject}`,
          content: replyText.trim() || 'Attachment included',
          messageType: 'direct_message',
          priority: 'normal',
          parentMessageId: rootId,
          metadata: replyAttachments.length > 0 ? { attachments: replyAttachments } : undefined,
        }),
      });
      if (res.ok) {
        setReplyText('');
        setReplyAttachments([]);
        toast.success('Reply sent');
        // Reload thread to show the new reply
        await loadThread(parentMsg.id);
      } else {
        const err = await res.json();
        toast.error(err.error || 'Failed to send reply');
      }
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setIsSendingReply(false);
    }
  };

  // Load thread when a message is selected
  useEffect(() => {
    if (selectedMessage) {
      setReplyText('');
      setReplyAttachments([]);
      loadThread(selectedMessage.id);
    } else {
      setThreadMessages([]);
      setReplyAttachments([]);
    }
  }, [selectedMessage?.id]);

  useEffect(() => {
    if (!selectedMessage || !pendingQuickReplyAttachment) return;
    setReplyAttachments((prev) => addAttachmentOnce(prev, pendingQuickReplyAttachment));
    setPendingQuickReplyAttachment(null);
    toast.success('Screenshot attached. You can now reply in this thread.');
  }, [pendingQuickReplyAttachment, selectedMessage]);

  const movePendingScreenshotToNewMessage = () => {
    if (!pendingQuickReplyAttachment) return;
    setComposeAttachments((prev) => addAttachmentOnce(prev, pendingQuickReplyAttachment));
    setPendingQuickReplyAttachment(null);
    setShowComposeModal(true);
    toast.success('Screenshot moved to new message');
  };

  const resolveFeedback = async (msg: Message, resolution: string) => {
    if (!resolution.trim()) { toast.error('Enter a resolution message'); return; }
    if (!msg.from.id) { toast.error('Could not determine message sender'); return; }
    setResolvingId(msg.id);
    try {
      const rootId = msg.parentMessageId || msg.id;
      const threadSubject = msg.title.startsWith('Re: ') ? msg.title : `Re: ${msg.title}`;
      const res = await fetch('/api/messaging', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recipientId: msg.from.id,
          subject: threadSubject,
          content: resolution,
          messageType: 'direct_message',
          priority: 'normal',
          parentMessageId: rootId,
        }),
      });
      if (res.ok) {
        await fetch(`/api/messaging/${msg.id}/read`, { method: 'PUT', headers: authHeaders({ 'Content-Type': 'application/json' }) });
        await Promise.all([loadThread(msg.id), loadMessages()]);
        toast.success('Resolved and reply added to this conversation');
        setResolutionText('');
      } else {
        const err = await res.json();
        toast.error(err.error || 'Failed to resolve');
      }
    } catch {
      toast.error('Failed to resolve');
    } finally {
      setResolvingId(null);
    }
  };

  const sendFeedback = async () => {
    if (!feedbackForm.subject || (!feedbackForm.content.trim() && feedbackAttachments.length === 0)) {
      toast.error('Add a message or attachment');
      return;
    }
    setIsSending(true);
    try {
      const res = await fetch('/api/messaging', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recipientId: availableRecipients.find((u: any) => !u.company_id)?.id,
          ...feedbackForm,
          content: feedbackForm.content.trim() || 'Attachment included',
          messageType: 'feedback',
          metadata: feedbackAttachments.length > 0 ? { attachments: feedbackAttachments } : undefined,
        }),
      });
      if (res.ok) {
        setFeedbackForm({ subject: '', content: '', priority: 'normal' });
        setFeedbackAttachments([]);
        setShowFeedbackModal(false);
        setActiveTab('sent');
        toast.success('Feedback sent!');
      } else {
        const err = await res.json();
        toast.error(err.error || 'Failed to send');
      }
    } catch {
      toast.error('Failed to send feedback');
    } finally {
      setIsSending(false);
    }
  };

  const sendMessage = async () => {
    if (!composeForm.recipientId) { toast.error('Select a recipient'); return; }
    if (!composeForm.subject || (!composeForm.content.trim() && composeAttachments.length === 0)) {
      toast.error('Add a message or attachment');
      return;
    }
    setIsSending(true);
    try {
      const res = await fetch('/api/messaging', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...composeForm,
          content: composeForm.content.trim() || 'Attachment included',
          metadata: composeAttachments.length > 0 ? { attachments: composeAttachments } : undefined,
        }),
      });
      if (res.ok) {
        setShowComposeModal(false);
        setComposeForm({ recipientId: '', subject: '', content: '', messageType: 'direct_message', priority: 'normal' });
        setComposeAttachments([]);
        setActiveTab('sent');
        toast.success('Message sent!');
      } else {
        const err = await res.json();
        toast.error(err.error || 'Failed to send');
      }
    } catch {
      toast.error('Failed to send');
    } finally {
      setIsSending(false);
    }
  };

  const openEditModal = (msg: Message, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    if (!canEditSentMessage(msg)) {
      toast.info('This message can no longer be edited because it already has a response.');
      return;
    }
    setEditingMessageId(msg.id);
    setEditForm({ subject: msg.title || '', content: msg.message || '' });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingMessageId(null);
    setEditForm({ subject: '', content: '' });
    setIsSavingEdit(false);
  };

  const saveEditedMessage = async () => {
    if (!editingMessageId) return;
    if (!editForm.subject.trim()) {
      toast.error('Subject is required');
      return;
    }

    const targetMessage = messages.find((m) => m.id === editingMessageId) || selectedMessage;
    const hasExistingAttachments = extractAttachments(targetMessage?.metadata).length > 0;
    if (!editForm.content.trim() && !hasExistingAttachments) {
      toast.error('Add a message or attachment');
      return;
    }

    setIsSavingEdit(true);
    try {
      const res = await fetch(`/api/messaging/${editingMessageId}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          subject: editForm.subject.trim(),
          content: editForm.content,
        }),
      });
      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(result.error || 'Failed to edit message');
        if (res.status === 409) {
          await loadMessages();
        }
        return;
      }

      setSelectedMessage((prev) => {
        if (!prev || prev.id !== editingMessageId) return prev;
        return {
          ...prev,
          title: editForm.subject.trim(),
          message: editForm.content,
          metadata: result?.data?.metadata || prev.metadata,
        };
      });

      toast.success('Message updated. Recipient has been notified that it was edited.');
      closeEditModal();
      await loadMessages();
      if (selectedMessage?.id === editingMessageId) {
        await loadThread(editingMessageId);
      }
    } catch {
      toast.error('Failed to edit message');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const callAI = async (action: string, instruction: string, target: 'compose' | 'feedback' | 'reply') => {
    setIsAILoading(true);
    try {
      const form = target === 'compose' ? composeForm : target === 'feedback' ? feedbackForm : null;
      const messageType = target === 'compose' ? (form as typeof composeForm).messageType : target === 'feedback' ? 'feedback' : 'reply';
      const content = target === 'reply' ? replyText : form!.content;
      const subject = target === 'reply' ? (selectedMessage?.title || '') : form!.subject;

      const res = await fetch('/api/messaging/ai-compose', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          action,
          instruction,
          subject,
          content,
          messageType,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'AI request failed');
      }

      const data = await res.json();
      if (data.success && (data.subject || data.content)) {
        if (target === 'compose') {
          setComposeForm((prev) => ({
            ...prev,
            ...(data.subject ? { subject: data.subject } : {}),
            ...(data.content ? { content: data.content } : {}),
          }));
        } else if (target === 'feedback') {
          setFeedbackForm((prev) => ({
            ...prev,
            ...(data.subject ? { subject: data.subject } : {}),
            ...(data.content ? { content: data.content } : {}),
          }));
        } else {
          if (data.content) setReplyText(data.content);
        }
        toast.success('Message updated by AI');
      } else {
        toast.error(data.message || 'Could not generate. Try different wording.');
      }
    } catch (e: any) {
      toast.error(e.message || 'AI assist failed.');
    } finally {
      setIsAILoading(false);
    }
  };

  const canSendMessages = () => {
    if (!messagingPermissions) return false;
    const p = messagingPermissions.canSendTo;
    return p.allUsers || p.sameCompany || p.auditors || p.admins || p.specificRoles?.length > 0;
  };

  // Derived counts — always in sync with the messages array, no stale state
  const unreadCount = messages.filter((m) => !m.isRead).length;
  const readCount = messages.filter((m) => m.isRead).length;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col bg-white overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
      {deepLinkMissing && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-3">
          <div className="text-amber-600 mt-0.5">⚠️</div>
          <div className="flex-1 text-sm text-amber-900">
            <div className="font-semibold">This message isn't in your inbox.</div>
            <div className="text-xs mt-0.5">
              Message <span className="font-mono">#{deepLinkMissing}</span> either wasn't sent to the account you're signed in with, was already deleted, or belongs to a different tenant. If you were emailed this link, check which account received the email and sign in with that one.
            </div>
          </div>
          <button
            onClick={() => {
              setDeepLinkMissing(null);
              const params = new URLSearchParams(location.search);
              params.delete('id');
              const qs = params.toString();
              navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
            }}
            className="text-amber-700 hover:text-amber-900 text-xs font-medium"
          >
            Dismiss
          </button>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
      {/* ── LEFT SIDEBAR ── */}
      <aside className="w-52 shrink-0 border-r border-slate-200 flex flex-col bg-white">
        <div className="px-4 py-5 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center shrink-0">
              <MessageSquare className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-slate-800 text-sm">Messages</span>
          </div>
          {canSendMessages() && (
            <button
              onClick={() => setShowComposeModal(true)}
              className="w-full flex items-center gap-2 justify-center bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Compose
            </button>
          )}
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {/* Inbox */}
          <button
            onClick={() => { setActiveTab('inbox'); setHasNewMessages(false); setSelectedMessage(null); clearIdFromUrl(); }}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
              activeTab === 'inbox' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-600 hover:bg-slate-50 font-medium'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <Inbox className="w-4 h-4" />
              Inbox
              {hasNewMessages && activeTab !== 'inbox' && (
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              )}
            </div>
            {inboxUnreadCount > 0 && (
              <span className="text-[10px] font-bold bg-blue-600 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none">
                {inboxUnreadCount}
              </span>
            )}
          </button>

          {/* Sent */}
          <button
            onClick={() => { setActiveTab('sent'); setSelectedMessage(null); clearIdFromUrl(); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              activeTab === 'sent' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-600 hover:bg-slate-50 font-medium'
            }`}
          >
            <Send className="w-4 h-4" />
            Sent
          </button>

          {/* Feedback */}
          <button
            onClick={() => { setActiveTab('feedback'); setSelectedMessage(null); clearIdFromUrl(); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              activeTab === 'feedback' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-600 hover:bg-slate-50 font-medium'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Feedback
          </button>
        </nav>

        {activeTab === 'feedback' && (
          <div className="px-2 pb-4">
            <button
              onClick={() => setShowFeedbackModal(true)}
              className="w-full flex items-center gap-2 justify-center bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-semibold hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Send Feedback
            </button>
          </div>
        )}

        {/* Stats footer */}
        <div className="px-4 py-3 border-t border-slate-100 space-y-1.5">
          <div className="flex justify-between text-[11px] text-slate-400">
            <span>Total</span>
            <span className="font-medium text-slate-600">{messages.length}</span>
          </div>
          <div className="flex justify-between text-[11px] text-slate-400">
            <span>Unread</span>
            <span className={`font-medium ${unreadCount > 0 ? 'text-blue-600' : 'text-slate-600'}`}>{unreadCount}</span>
          </div>
        </div>
      </aside>

      {/* ── MESSAGE LIST ── */}
      <div className="w-80 shrink-0 border-r border-slate-200 flex flex-col overflow-hidden bg-white">
        {/* Search + filter */}
        <div className="px-3 pt-3 pb-2 border-b border-slate-100 bg-white">
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search messages..."
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none focus:bg-white"
            />
          </div>
          <div className="flex gap-1">
            {(['all', 'unread', 'read'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilterType(f)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                  filterType === f ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                {f === 'all' ? 'All' : f === 'unread' ? `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}` : `Read (${readCount})`}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="animate-spin text-slate-300" size={20} />
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center mb-3">
                <MessageSquare className="w-6 h-6 text-slate-200" />
              </div>
              <p className="text-sm font-medium text-slate-400">No messages</p>
              <p className="text-xs text-slate-300 mt-1">
                {searchTerm || filterType !== 'all' ? 'Try adjusting your filters' : `Your ${activeTab} is empty`}
              </p>
            </div>
          ) : (
            filteredMessages.map((msg) => {
              const isSelected = selectedMessage?.id === msg.id;
              const person = activeTab === 'sent' && msg.to ? msg.to.name : msg.from.name;
              const initials = person
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase() || '?';
              const preview = msg.message.replace(/<[^>]+>/g, '').slice(0, 80);
              const hasAttachments = extractAttachments(msg.metadata).length > 0;

              return (
                <div
                  key={msg.id}
                  onClick={() => { setSelectedMessage(msg); if (!msg.isRead) markAsRead(msg.id); }}
                  className={`group relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-blue-50 border-r-2 border-blue-500'
                      : !msg.isRead
                      ? 'hover:bg-slate-50'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  {/* Unread indicator */}
                  {!msg.isRead && !isSelected && (
                    <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  )}

                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white text-[11px] font-bold shrink-0 mt-0.5">
                    {initials}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-1 mb-0.5">
                      <span className={`text-sm truncate ${!msg.isRead ? 'font-semibold text-slate-900' : 'font-medium text-slate-600'}`}>
                        {person}
                      </span>
                      <div className="flex items-center gap-1 shrink-0 ml-1">
                        {msg.isSent && (
                          <span title={msg.readByRecipient ? 'Read by recipient' : 'Delivered'}>
                            {msg.readByRecipient
                              ? <svg className="w-3 h-3 text-blue-500" viewBox="0 0 16 16" fill="currentColor"><path d="M1 8l4 4L10 5m3-2l-6 7-2-2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 8l4 4L15 5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              : <svg className="w-3 h-3 text-slate-400" viewBox="0 0 16 16" fill="none"><path d="M1 8l4 4 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            }
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400">{formatTimestamp(msg.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 mb-0.5 min-w-0">
                      <p className={`text-xs truncate ${!msg.isRead ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                        {msg.title}
                      </p>
                      {isMessageEdited(msg.metadata) && (
                        <span className="text-[9px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1 py-0.5 shrink-0">
                          Edited
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-[11px] text-slate-400 truncate leading-relaxed flex-1">{preview}</p>
                      {hasAttachments && (
                        <span className="text-[10px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full shrink-0 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          File
                        </span>
                      )}
                      {msg.replyCount && msg.replyCount > 0 ? (
                        <span className="text-[10px] font-medium text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-full shrink-0">
                          {msg.replyCount} {msg.replyCount === 1 ? 'reply' : 'replies'}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canEditSentMessage(msg) && (
                      <button
                        onClick={(e) => openEditModal(msg, e)}
                        title="Edit"
                        className="p-1 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => deleteMessage(e, msg.id)}
                      disabled={deletingId === msg.id}
                      title="Delete"
                      className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded disabled:opacity-30"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* List footer */}
        <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between">
          <span className="text-[11px] text-slate-400">{filteredMessages.length} messages</span>
          <button
            onClick={loadMessages}
            className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>
      </div>

      {/* ── MESSAGE DETAIL ── */}
      <main className="flex-1 flex flex-col overflow-hidden bg-white">
        {selectedMessage ? (
          <>
            {/* Detail header */}
            <div className="px-6 py-4 border-b border-slate-100 bg-white">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-slate-900 leading-tight mb-1">{selectedMessage.title}</h2>
                  <div className="flex items-center flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                    <span>
                      {activeTab === 'sent' && selectedMessage.to
                        ? `To: ${selectedMessage.to.name}`
                        : `From: ${selectedMessage.from.name}`}
                    </span>
                    <span>{new Date(selectedMessage.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    {isMessageEdited(selectedMessage.metadata) && (
                      <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 font-medium">
                        Edited
                        {getMessageEditedAt(selectedMessage.metadata) && (
                          <span>
                            {new Date(String(getMessageEditedAt(selectedMessage.metadata))).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        )}
                      </span>
                    )}
                    {selectedMessage.priority === 'high' && (
                      <span className="flex items-center gap-1 text-red-600 font-medium">
                        <AlertTriangle className="w-3 h-3" />
                        High priority
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {activeTab === 'sent' && canEditSentMessage(selectedMessage) && (
                    <button
                      onClick={() => openEditModal(selectedMessage)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => { setSelectedMessage(null); clearIdFromUrl(); }}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Detail body — threaded conversation */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {threadLoading ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw className="animate-spin text-slate-300" size={20} />
                </div>
              ) : threadMessages.length > 1 ? (
                /* ── Thread view: multiple messages ── */
                <div className="space-y-4">
                  {threadMessages.map((tm) => {
                    const isMine = tm.senderId === user?.id;
                    const senderName = [tm.sender?.firstName, tm.sender?.lastName].filter(Boolean).join(' ') || tm.sender?.email || 'Unknown';
                    const initials = senderName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                    const attachments = extractAttachments(tm.metadata);
                    return (
                      <div key={tm.id} className={`flex gap-3 ${isMine ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0 ${isMine ? 'bg-blue-500' : 'bg-slate-500'}`}>
                          {initials}
                        </div>
                        <div className={`max-w-[75%] text-left ${isMine ? 'items-end' : 'items-start'}`}>
                          <div className={`flex items-center gap-2 mb-1 ${isMine ? 'justify-end' : ''}`}>
                            <span className="text-xs font-semibold text-slate-700">{isMine ? 'You' : senderName}</span>
                            <span className="text-[10px] text-slate-400">
                              {new Date(tm.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isMessageEdited(tm.metadata) && (
                              <span className="text-[10px] font-medium text-amber-700 bg-amber-100 border border-amber-200 rounded px-1 py-0.5">
                                Edited
                              </span>
                            )}
                          </div>
                          <div className={`px-4 py-2.5 rounded-2xl text-left text-sm leading-relaxed whitespace-pre-wrap ${
                            isMine
                              ? 'bg-blue-600 text-white rounded-tr-md'
                              : 'bg-slate-100 text-slate-800 rounded-tl-md'
                          }`}>
                            {renderMessageContentWithLinks(
                              tm.content,
                              isMine
                                ? 'underline text-blue-100 hover:text-white break-all'
                                : 'underline text-blue-700 hover:text-blue-800 break-all'
                            )}
                          </div>
                          {attachments.length > 0 && (
                            <div className={`mt-2 space-y-1.5 ${isMine ? 'flex flex-col items-end' : ''}`}>
                              {attachments.map((attachment, index) => (
                                <button
                                  key={`${tm.id}-attachment-${index}`}
                                  onClick={() => downloadAttachment(attachment)}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${
                                    isMine
                                      ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                  }`}
                                >
                                  <Paperclip className="w-3.5 h-3.5" />
                                  <span className="truncate max-w-[240px] text-left">{attachment.originalName || attachment.fileName}</span>
                                  <span className="text-[10px] opacity-70">{formatFileSize(Number(attachment.size || 0))}</span>
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              ))}
                            </div>
                          )}
                          {/* Read receipt — shown below messages I sent */}
                          <ReadReceipt isRead={tm.isRead} readAt={tm.readAt} isMine={isMine} />

                          {/* Reaction row — existing reactions as pills + a quick picker.
                              Click a pill to toggle (add/remove) your own reaction.
                              The picker always shows the 6 REACTION_EMOJIS so the user
                              can add a new type without leaving the thread. */}
                          <div className={`mt-1.5 flex items-center gap-1.5 flex-wrap ${isMine ? 'justify-end' : ''}`}>
                            {(tm.reactions ?? []).map(r => (
                              <button
                                key={`${tm.id}-${r.emoji}`}
                                onClick={() => toggleReaction(tm.id, r.emoji)}
                                title={r.reacted_by_me ? 'Click to remove your reaction' : 'Click to react'}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] transition-colors ${
                                  r.reacted_by_me
                                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                }`}
                              >
                                <span>{r.emoji}</span>
                                <span className="font-semibold">{r.count}</span>
                              </button>
                            ))}
                            {/* Quick add picker — hover / always-visible micro row */}
                            <div className="inline-flex items-center gap-0.5 pl-1">
                              {REACTION_EMOJIS.filter(e => !(tm.reactions ?? []).some(r => r.emoji === e && r.reacted_by_me)).slice(0, 4).map(e => (
                                <button
                                  key={`${tm.id}-picker-${e}`}
                                  onClick={() => toggleReaction(tm.id, e)}
                                  title={`React with ${e}`}
                                  className="w-6 h-6 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-[13px] opacity-60 hover:opacity-100"
                                >
                                  {e}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={threadEndRef} />
                </div>
              ) : (
                /* ── Single message view (no thread yet) ── */
                <>
                  <div className="flex items-center gap-3 mb-6 pb-5 border-b border-slate-100">
                    <div className="w-10 h-10 rounded-full bg-slate-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
                      {(activeTab === 'sent' && selectedMessage.to
                        ? selectedMessage.to.name
                        : selectedMessage.from.name)?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">
                        {activeTab === 'sent' && selectedMessage.to
                          ? selectedMessage.to.name
                          : selectedMessage.from.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(selectedMessage.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {isMessageEdited(selectedMessage.metadata) && (
                        <p className="text-[11px] text-amber-700 font-medium mt-0.5">
                          Edited
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap text-left">
                    {renderMessageContentWithLinks(
                      selectedMessage.message,
                      'underline text-blue-700 hover:text-blue-800 break-all'
                    )}
                  </div>
                  {/* Read receipt for single-message view (only on messages I sent) */}
                  {selectedMessage.isSent && threadMessages.length === 1 && (
                    <ReadReceipt
                      isRead={threadMessages[0]?.isRead ?? selectedMessage.readByRecipient}
                      readAt={threadMessages[0]?.readAt ?? null}
                      isMine={true}
                    />
                  )}
                  {extractAttachments(selectedMessage.metadata).length > 0 && (
                    <div className="mt-4 space-y-2">
                      {extractAttachments(selectedMessage.metadata).map((attachment, index) => (
                        <button
                          key={`single-attachment-${index}`}
                          onClick={() => downloadAttachment(attachment)}
                          className="w-full max-w-md flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-700 hover:bg-slate-100"
                        >
                          <Paperclip className="w-3.5 h-3.5" />
                          <span className="truncate flex-1 text-left">{attachment.originalName || attachment.fileName}</span>
                          <span className="text-[10px] text-slate-500">{formatFileSize(Number(attachment.size || 0))}</span>
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedMessage.related && (
                    <div className="mt-5 bg-slate-50 rounded-lg px-4 py-3 border border-slate-200 text-sm text-slate-600">
                      <strong className="font-medium">Related:</strong> {selectedMessage.related.name}
                    </div>
                  )}
                </>
              )}

              {/* Feedback resolution panel (admin only) */}
              {isSystemAdmin && selectedMessage.type === 'feedback' && activeTab === 'inbox' && (
                <div className="mt-8 p-5 bg-emerald-50 border border-emerald-200 rounded-xl max-w-2xl">
                  <h4 className="text-sm font-bold text-emerald-800 mb-1 flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Resolve Feedback — Reply to User
                  </h4>
                  <p className="text-xs text-emerald-600 mb-3">
                    Write a resolution message. It will be added to this same conversation and the feedback will be marked resolved.
                  </p>
                  <textarea
                    value={resolutionText}
                    onChange={(e) => setResolutionText(e.target.value)}
                    rows={4}
                    placeholder="Thank you for your feedback. We have..."
                    className="w-full px-3 py-2 border border-emerald-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white resize-none"
                  />
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={() => resolveFeedback(selectedMessage, resolutionText)}
                      disabled={!resolutionText.trim() || resolvingId === selectedMessage.id}
                      className="px-5 py-1.5 text-sm font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {resolvingId === selectedMessage.id ? (
                        <><RefreshCw size={14} className="animate-spin" /> Resolving...</>
                      ) : (
                        <><Send size={14} /> Mark Resolved & Reply</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Inline reply bar (not for feedback with resolution panel) ── */}
            {!(isSystemAdmin && selectedMessage.type === 'feedback' && activeTab === 'inbox') && selectedMessage.type !== 'feedback' && (
              <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 mb-14">
                <input
                  ref={replyFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    void handleAttachmentPick('reply', file);
                    e.currentTarget.value = '';
                  }}
                />
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => replyFileInputRef.current?.click()}
                    disabled={uploadingReplyAttachment}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {uploadingReplyAttachment ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                    Attach file
                  </button>
                  <button
                    onClick={() => captureAndAttachScreenshot('reply')}
                    disabled={uploadingReplyAttachment}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    Screenshot
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleDictation('reply', replyText)}
                    disabled={!dictationSupported}
                    title={dictationSupported ? (recordingTarget === 'reply' ? 'Stop recording' : 'Record voice to text') : 'Voice recording not supported in this browser'}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border disabled:opacity-50 transition-colors ${
                      recordingTarget === 'reply'
                        ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Mic className={`w-3.5 h-3.5 ${recordingTarget === 'reply' ? 'animate-pulse' : ''}`} />
                    {recordingTarget === 'reply' ? 'Stop' : 'Record'}
                  </button>
                </div>
                {replyAttachments.length > 0 && (
                  <div className="mb-2 space-y-1.5">
                    {replyAttachments.map((attachment, index) => (
                      <div key={`reply-attachment-${index}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs">
                        <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                        <span className="truncate flex-1 text-slate-700">{attachment.originalName || attachment.fileName}</span>
                        <span className="text-[10px] text-slate-500">{formatFileSize(Number(attachment.size || 0))}</span>
                        <button onClick={() => removeAttachment('reply', index)} className="text-slate-400 hover:text-red-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-3 mr-16">
                  <textarea
                    ref={replyTextareaRef}
                    value={replyText}
                    onChange={(e) => {
                      setReplyText(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 256) + 'px';
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendReply(selectedMessage);
                      }
                    }}
                    rows={1}
                    placeholder="Type a reply..."
                    className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white resize-none overflow-y-auto"
                    style={{ minHeight: '40px', maxHeight: '256px' }}
                  />
                  <button
                    onClick={() => sendReply(selectedMessage)}
                    disabled={isSendingReply || uploadingReplyAttachment || (!replyText.trim() && replyAttachments.length === 0)}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                  >
                    {isSendingReply ? (
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                {/* Inline AI toolbar */}
                <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 mt-1.5 border border-slate-100 rounded-lg bg-slate-50">
                  <Sparkles className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                  <button type="button" onClick={() => callAI('generate', replyText, 'reply')} disabled={isAILoading || !replyText.trim()}
                    title="Generate a professional message from your text" className="text-[11px] font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-2 py-0.5 rounded disabled:opacity-40 transition-colors flex-shrink-0">
                    Generate
                  </button>
                  <span className="w-px h-3 bg-slate-200 flex-shrink-0" />
                  <button type="button" onClick={() => callAI('expand', '', 'reply')} disabled={isAILoading || !replyText.trim()}
                    title="Expand" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                    <Sparkles className="w-2.5 h-2.5" /> Expand
                  </button>
                  <button type="button" onClick={() => callAI('improve', '', 'reply')} disabled={isAILoading || !replyText.trim()}
                    title="Improve" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                    <Wand2 className="w-2.5 h-2.5" /> Improve
                  </button>
                  <button type="button" onClick={() => callAI('summarise', '', 'reply')} disabled={isAILoading || !replyText.trim()}
                    title="Summarise" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                    <RefreshCw className="w-2.5 h-2.5" /> Summarise
                  </button>
                  <button type="button" onClick={() => callAI('rewrite', '', 'reply')} disabled={isAILoading || !replyText.trim()}
                    title="Rewrite" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                    <RotateCcw className="w-2.5 h-2.5" /> Rewrite
                  </button>
                  {isAILoading && <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-500 border-t-transparent flex-shrink-0 ml-auto" />}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 ml-1">Press Enter to send, Shift+Enter for new line</p>
              </div>
            )}
          </>
        ) : (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 border border-slate-100">
              <MessageSquare className="w-8 h-8 text-slate-200" />
            </div>
            <p className="text-sm font-semibold text-slate-400">Select a message to read</p>
            <p className="text-xs text-slate-300 mt-1.5">Click any message in the list to view its contents</p>
            {pendingQuickReplyAttachment && (
              <div className="mt-5 w-full max-w-lg rounded-xl border border-amber-200 bg-amber-50 p-4 text-left">
                <p className="text-sm font-semibold text-amber-900">Screenshot is ready for an existing conversation</p>
                <p className="text-xs text-amber-800 mt-1">
                  Open any message thread to attach it to your reply, or move it into a new message.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={movePendingScreenshotToNewMessage}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-amber-700 rounded-lg hover:bg-amber-800"
                  >
                    Use In New Message
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingQuickReplyAttachment(null)}
                    className="px-3 py-1.5 text-xs font-medium text-amber-900 border border-amber-300 rounded-lg hover:bg-amber-100"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            {canSendMessages() && (
              <button
                onClick={() => setShowComposeModal(true)}
                className="mt-6 flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Compose new message
              </button>
            )}
          </div>
        )}
      </main>

      {/* ── SCREENSHOT MARKUP MODAL ── */}
      {screenshotEditorImage && screenshotEditorTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-base font-bold text-slate-900">Mark Up Screenshot</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Drag on the screenshot to highlight issue areas before attaching.
                </p>
              </div>
              <button
                onClick={closeScreenshotEditor}
                disabled={savingMarkedScreenshot}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-4">
              <div className="inline-block relative border border-slate-200 rounded-lg overflow-hidden max-w-full">
                <img
                  src={screenshotEditorImage}
                  alt="Captured screenshot"
                  className="block max-w-full max-h-[64vh] select-none"
                  draggable={false}
                />
                <div
                  ref={screenshotOverlayRef}
                  className="absolute inset-0 cursor-crosshair select-none"
                  onMouseDown={beginScreenshotHighlight}
                  onMouseMove={moveScreenshotHighlight}
                  onMouseUp={endScreenshotHighlight}
                  onMouseLeave={endScreenshotHighlight}
                >
                  {screenshotHighlights.map((highlight, index) => (
                    <div
                      key={`screenshot-highlight-${index}`}
                      className="absolute border-2 border-amber-500 bg-amber-300/25"
                      style={highlightToStyle(highlight)}
                    >
                      <div className="absolute -top-5 left-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                  {draftScreenshotHighlight && (
                    <div
                      className="absolute border-2 border-amber-500 border-dashed bg-amber-200/30"
                      style={highlightToStyle(draftScreenshotHighlight)}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-3">
              <div className="text-xs text-slate-500">
                {screenshotHighlights.length} highlight{screenshotHighlights.length === 1 ? '' : 's'} added
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setScreenshotHighlights((prev) => prev.slice(0, -1))}
                  disabled={savingMarkedScreenshot || screenshotHighlights.length === 0}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  Undo
                </button>
                <button
                  type="button"
                  onClick={() => { setScreenshotHighlights([]); setDraftScreenshotHighlight(null); }}
                  disabled={savingMarkedScreenshot || (screenshotHighlights.length === 0 && !draftScreenshotHighlight)}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={closeScreenshotEditor}
                  disabled={savingMarkedScreenshot}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveMarkedScreenshotAndAttach}
                  disabled={savingMarkedScreenshot}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingMarkedScreenshot ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                  Attach Marked Screenshot
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT MESSAGE MODAL ── */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeEditModal}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-base font-bold text-slate-900">Edit Message</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  You can edit this message until the recipient sends a response. They will be notified that it was edited.
                </p>
              </div>
              <button
                onClick={closeEditModal}
                disabled={isSavingEdit}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Subject</label>
                <input
                  type="text"
                  value={editForm.subject}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, subject: e.target.value }))}
                  placeholder="Message subject..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Message</label>
                <textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, content: e.target.value }))}
                  rows={8}
                  placeholder="Write your message..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
              <button
                onClick={closeEditModal}
                disabled={isSavingEdit}
                className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEditedMessage}
                disabled={isSavingEdit || !editForm.subject.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingEdit ? (
                  <><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />Saving...</>
                ) : (
                  <><Pencil className="w-3.5 h-3.5" />Save Changes</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      </div> {/* end of flex flex-1 overflow-hidden wrapper for sidebar+main */}

      {/* ── COMPOSE MODAL ── */}
      {showComposeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">New Message</h2>
              <button onClick={() => { setShowComposeModal(false); setComposeAttachments([]); }} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Recipient */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">To</label>
                <select
                  value={composeForm.recipientId}
                  onChange={(e) => setComposeForm((p) => ({ ...p, recipientId: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select recipient...</option>
                  {availableRecipients.map((u: any) => {
                    const roleLabel = u.persona
                      ? u.persona
                      : !u.company_id ? 'Admin' : '';
                    const scopeLabel = u.company_name ? ` — ${u.company_name}` : '';
                    return (
                      <option key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                        {roleLabel ? ` · ${roleLabel}` : ''}
                        {scopeLabel} · {u.email}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Type + Priority row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Type</label>
                  <select
                    value={composeForm.messageType}
                    onChange={(e) => setComposeForm((p) => ({ ...p, messageType: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="direct_message">Direct Message</option>
                    <option value="compliance_request">Compliance Request</option>
                    {(isSystemAdmin || isCompanyAdmin) && (
                      <>
                        <option value="notification">Notification</option>
                        <option value="reminder">Reminder</option>
                      </>
                    )}
                    {isSystemAdmin && (
                      <>
                        <option value="feedback">Feedback</option>
                        <option value="system_alert">System Alert</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Priority</label>
                  <select
                    value={composeForm.priority}
                    onChange={(e) => setComposeForm((p) => ({ ...p, priority: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Subject</label>
                <input
                  type="text"
                  value={composeForm.subject}
                  onChange={(e) => setComposeForm((p) => ({ ...p, subject: e.target.value }))}
                  placeholder="Message subject..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <input
                ref={composeFileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  void handleAttachmentPick('compose', file);
                  e.currentTarget.value = '';
                }}
              />

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Attachments</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => composeFileInputRef.current?.click()}
                    disabled={uploadingComposeAttachment}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {uploadingComposeAttachment ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                    Attach file
                  </button>
                  <button
                    type="button"
                    onClick={() => captureAndAttachScreenshot('compose')}
                    disabled={uploadingComposeAttachment}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    Capture screen
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleDictation('compose', composeForm.content)}
                    disabled={!dictationSupported}
                    title={dictationSupported ? (recordingTarget === 'compose' ? 'Stop recording' : 'Record voice to text') : 'Voice recording not supported in this browser'}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border disabled:opacity-50 transition-colors ${
                      recordingTarget === 'compose'
                        ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Mic className={`w-3.5 h-3.5 ${recordingTarget === 'compose' ? 'animate-pulse' : ''}`} />
                    {recordingTarget === 'compose' ? 'Stop recording' : 'Record voice'}
                  </button>
                </div>
                {composeAttachments.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {composeAttachments.map((attachment, index) => (
                      <div key={`compose-attachment-${index}`} className="flex items-center gap-2 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs">
                        <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                        <span className="truncate flex-1 text-slate-700">{attachment.originalName || attachment.fileName}</span>
                        <span className="text-[10px] text-slate-500">{formatFileSize(Number(attachment.size || 0))}</span>
                        <button type="button" onClick={() => removeAttachment('compose', index)} className="text-slate-400 hover:text-red-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Content */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Message</label>
                <div className="border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 overflow-hidden">
                  <textarea
                    value={composeForm.content}
                    onChange={(e) => setComposeForm((p) => ({ ...p, content: e.target.value }))}
                    rows={6}
                    placeholder="Write your message..."
                    className="w-full px-3 py-2 text-sm focus:outline-none resize-none border-0"
                  />
                  {/* Inline AI toolbar */}
                  <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-t border-slate-100 bg-slate-50">
                    <Sparkles className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                    <button type="button" onClick={() => callAI('generate', composeForm.content, 'compose')} disabled={isAILoading || !composeForm.content}
                      title="Generate a professional message from your text" className="text-[11px] font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-2 py-0.5 rounded disabled:opacity-40 transition-colors flex-shrink-0">
                      Generate
                    </button>
                    <span className="w-px h-3 bg-slate-200 flex-shrink-0" />
                    <button type="button" onClick={() => callAI('expand', '', 'compose')} disabled={isAILoading || !composeForm.content}
                      title="Expand" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                      <Sparkles className="w-2.5 h-2.5" /> Expand
                    </button>
                    <button type="button" onClick={() => callAI('improve', '', 'compose')} disabled={isAILoading || !composeForm.content}
                      title="Improve" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                      <Wand2 className="w-2.5 h-2.5" /> Improve
                    </button>
                    <button type="button" onClick={() => callAI('summarise', '', 'compose')} disabled={isAILoading || !composeForm.content}
                      title="Summarise" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                      <RefreshCw className="w-2.5 h-2.5" /> Summarise
                    </button>
                    <button type="button" onClick={() => callAI('rewrite', '', 'compose')} disabled={isAILoading || !composeForm.content}
                      title="Rewrite" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                      <RotateCcw className="w-2.5 h-2.5" /> Rewrite
                    </button>
                    {isAILoading && <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-500 border-t-transparent flex-shrink-0 ml-auto" />}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
              <button
                onClick={() => { setShowComposeModal(false); setComposeAttachments([]); }}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={sendMessage}
                disabled={isSending || uploadingComposeAttachment || !composeForm.recipientId || !composeForm.subject || (!composeForm.content.trim() && composeAttachments.length === 0)}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />Sending...</>
                ) : (
                  <><Send className="w-3.5 h-3.5" />Send</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FEEDBACK MODAL ── */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-base font-bold text-slate-900">Send Feedback</h2>
                <p className="text-xs text-slate-500 mt-0.5">Sent directly to the System Administrator</p>
              </div>
              <button onClick={() => { setShowFeedbackModal(false); setFeedbackAttachments([]); }} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Subject</label>
                <input
                  type="text"
                  value={feedbackForm.subject}
                  onChange={(e) => setFeedbackForm((p) => ({ ...p, subject: e.target.value }))}
                  placeholder="Brief description..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Priority</label>
                <select
                  value={feedbackForm.priority}
                  onChange={(e) => setFeedbackForm((p) => ({ ...p, priority: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <input
                ref={feedbackFileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  void handleAttachmentPick('feedback', file);
                  e.currentTarget.value = '';
                }}
              />

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Attachments</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => feedbackFileInputRef.current?.click()}
                    disabled={uploadingFeedbackAttachment}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {uploadingFeedbackAttachment ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                    Attach file
                  </button>
                  <button
                    type="button"
                    onClick={() => captureAndAttachScreenshot('feedback')}
                    disabled={uploadingFeedbackAttachment}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    Capture screen
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleDictation('feedback', feedbackForm.content)}
                    disabled={!dictationSupported}
                    title={dictationSupported ? (recordingTarget === 'feedback' ? 'Stop recording' : 'Record voice to text') : 'Voice recording not supported in this browser'}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border disabled:opacity-50 transition-colors ${
                      recordingTarget === 'feedback'
                        ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Mic className={`w-3.5 h-3.5 ${recordingTarget === 'feedback' ? 'animate-pulse' : ''}`} />
                    {recordingTarget === 'feedback' ? 'Stop recording' : 'Record voice'}
                  </button>
                </div>
                {feedbackAttachments.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {feedbackAttachments.map((attachment, index) => (
                      <div key={`feedback-attachment-${index}`} className="flex items-center gap-2 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs">
                        <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                        <span className="truncate flex-1 text-slate-700">{attachment.originalName || attachment.fileName}</span>
                        <span className="text-[10px] text-slate-500">{formatFileSize(Number(attachment.size || 0))}</span>
                        <button type="button" onClick={() => removeAttachment('feedback', index)} className="text-slate-400 hover:text-red-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Message</label>
                <div className="border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-emerald-500 overflow-hidden">
                  <textarea
                    value={feedbackForm.content}
                    onChange={(e) => setFeedbackForm((p) => ({ ...p, content: e.target.value }))}
                    rows={6}
                    placeholder="Describe your feedback in detail..."
                    className="w-full px-3 py-2 text-sm focus:outline-none resize-none border-0"
                  />
                  {/* Inline AI toolbar */}
                  <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-t border-slate-100 bg-slate-50">
                    <Sparkles className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                    <button type="button" onClick={() => callAI('generate', feedbackForm.content, 'feedback')} disabled={isAILoading || !feedbackForm.content}
                      title="Generate a professional message from your text" className="text-[11px] font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-2 py-0.5 rounded disabled:opacity-40 transition-colors flex-shrink-0">
                      Generate
                    </button>
                    <span className="w-px h-3 bg-slate-200 flex-shrink-0" />
                    <button type="button" onClick={() => callAI('expand', '', 'feedback')} disabled={isAILoading || !feedbackForm.content}
                      title="Expand" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                      <Sparkles className="w-2.5 h-2.5" /> Expand
                    </button>
                    <button type="button" onClick={() => callAI('improve', '', 'feedback')} disabled={isAILoading || !feedbackForm.content}
                      title="Improve" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                      <Wand2 className="w-2.5 h-2.5" /> Improve
                    </button>
                    <button type="button" onClick={() => callAI('summarise', '', 'feedback')} disabled={isAILoading || !feedbackForm.content}
                      title="Summarise" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                      <RefreshCw className="w-2.5 h-2.5" /> Summarise
                    </button>
                    <button type="button" onClick={() => callAI('rewrite', '', 'feedback')} disabled={isAILoading || !feedbackForm.content}
                      title="Rewrite" className="text-[11px] text-slate-500 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5">
                      <RotateCcw className="w-2.5 h-2.5" /> Rewrite
                    </button>
                    {isAILoading && <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-500 border-t-transparent flex-shrink-0 ml-auto" />}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
              <button
                onClick={() => { setShowFeedbackModal(false); setFeedbackAttachments([]); }}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={sendFeedback}
                disabled={isSending || uploadingFeedbackAttachment || !feedbackForm.subject || (!feedbackForm.content.trim() && feedbackAttachments.length === 0)}
                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />Sending...</>
                ) : (
                  <><MessageSquare className="w-3.5 h-3.5" />Send Feedback</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
